import { useCallback } from "react";
import Button from "@ui-kit/form/Button/Button.web";
import CartTotal from "@app-contexts/public/features/basket/components/CartTotal/CartTotal.web";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import useRedirect from "@shared/hooks/useRedirect";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useTranslation } from "@shared/utils/internationalization";
import { NestedPublicVenueSerializer } from "@shared/modules/events/types/public";
import { CheckoutLabel } from "../shared/CheckoutLabel.web";

export interface ModalFooterButtonsProps {
  venue?: NestedPublicVenueSerializer;
  onClose: () => void;
  isDynamicHold?: boolean;
  hasAdditionalItems?: boolean;
}

export function ModalFooterButtons({
  venue = undefined,
  onClose,
  isDynamicHold = false,
  hasAdditionalItems = false,
}: ModalFooterButtonsProps): JSX.Element {
  const { t } = useTranslation("common");
  const { redirect } = useRedirect();
  const { hasItems, isBasketUpdating } = usePublicBasket();
  const navigateToCheckout = useCallback(
    () => redirect(PUBLIC_ROUTES.CHECKOUT.ROOT),
    [redirect]
  );

  const onModalClose = useCallback(() => {
    if (isDynamicHold) {
      navigateToCheckout();
      return;
    }

    onClose();
  }, [isDynamicHold, onClose, navigateToCheckout]);

  return (
    <Flex
      width="full"
      alignItems="center"
      justifyContent={{ base: "space-between", xs: "flex-end" }}
    >
      {isDynamicHold ? (
        <Button
          data-testid={
            !hasAdditionalItems
              ? "modal-footer-buttons-close"
              : "modal-footer-buttons-continue-shopping"
          }
          variant="outline"
          colorPalette="primary"
          // TODO: Add element token for 140px (between element.8xl=128px and element.9xl=160px)
          // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
          minWidth="140px"
          onClick={onClose}
          mr="auto"
        >
          {!hasAdditionalItems ? t("Close") : t("Continue Shopping")}
        </Button>
      ) : (
        <Box mr="lg">
          <CartTotal venue={venue} includeTotal={false} />
        </Box>
      )}

      <Button
        data-testid={
          isDynamicHold
            ? "modal-footer-buttons-checkout"
            : "modal-footer-buttons-continue"
        }
        variant="solid"
        colorPalette="primary"
        // TODO: Add element token for 140px (between element.8xl=128px and element.9xl=160px)
        // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
        minWidth="140px"
        onClick={onModalClose}
        isLoading={isBasketUpdating}
        disabled={!hasItems || isBasketUpdating}
      >
        {isDynamicHold ? <CheckoutLabel /> : t("Continue")}
      </Button>
    </Flex>
  );
}

export default ModalFooterButtons;
